import { BRAND } from "./brand";

export { BRAND };

/** Bottom margin reserved for branded footer (mm). */
export const PDF_BRAND_FOOTER_MARGIN = 18;

async function loadLogo() {
  try {
    const res = await fetch(BRAND.logoSrc);
    if (!res.ok) return null;
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const dims = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({
          width: img.naturalWidth || img.width,
          height: img.naturalHeight || img.height,
        });
      img.onerror = () => resolve(null);
      img.src = dataUrl;
    });
    if (!dims?.width || !dims?.height) return { dataUrl, width: 0, height: 0 };
    return { dataUrl, ...dims };
  } catch {
    return null;
  }
}

/**
 * Preload logo + computed header metrics for sync drawing on every page.
 * Pass a jsPDF instance so tagline width can be measured.
 */
export async function loadPdfBrandAssets(doc) {
  const logoY = 8;
  let brandW = 54;
  let logoH = 12;
  let logoW = 54;
  let logoBottomY = logoY + logoH;
  let logoDataUrl = null;
  let useTextBrand = false;

  const logo = await loadLogo();
  if (logo?.dataUrl && logo.width > 0 && logo.height > 0) {
    const maxH = 12;
    const maxW = 54;
    const ratio = logo.width / logo.height;
    logoH = maxH;
    logoW = logoH * ratio;
    if (logoW > maxW) {
      logoW = maxW;
      logoH = logoW / ratio;
    }
    brandW = logoW;
    logoBottomY = logoY + logoH;
    logoDataUrl = logo.dataUrl;
  } else {
    useTextBrand = true;
    logoBottomY = 18;
  }

  doc.setFont("helvetica", "normal");
  let taglineSize = 11;
  doc.setFontSize(taglineSize);
  const taglineW = doc.getTextWidth(BRAND.tagline);
  if (taglineW > 0 && brandW > 0) {
    taglineSize = Math.max(7, Math.min(12, taglineSize * (brandW / taglineW)));
    doc.setFontSize(taglineSize);
    while (doc.getTextWidth(BRAND.tagline) > brandW && taglineSize > 6.5) {
      taglineSize -= 0.2;
      doc.setFontSize(taglineSize);
    }
  }

  const taglineY = logoBottomY + 5.5;
  const headerBottomPad = 4;
  const headerH = taglineY + headerBottomPad;

  return {
    logoDataUrl,
    logoW,
    logoH,
    logoY,
    taglineSize,
    taglineY,
    headerH,
    useTextBrand,
  };
}

/**
 * Draw a small centered logo only (no lavender band, no tagline).
 * Returns the Y just below the logo for continuing content.
 */
export function drawPdfBrandLogoOnly(
  doc,
  assets,
  { startY, maxH = 8, gapAfter = 3.5 } = {},
) {
  const pageW = doc.internal.pageSize.getWidth();
  const { logoDataUrl, logoW, logoH, useTextBrand } = assets;

  if (logoDataUrl && logoW > 0 && logoH > 0) {
    const ratio = logoW / logoH;
    let h = maxH;
    let w = h * ratio;
    const maxW = 36;
    if (w > maxW) {
      w = maxW;
      h = w / ratio;
    }
    try {
      doc.addImage(logoDataUrl, "PNG", (pageW - w) / 2, startY, w, h);
      return startY + h + gapAfter;
    } catch {
      // fall through to text brand
    }
  }

  if (useTextBrand || !logoDataUrl) {
    doc.setTextColor(48, 45, 69);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(BRAND.name, pageW / 2, startY + 4, { align: "center" });
    return startY + 4 + gapAfter;
  }

  return startY;
}

/** Draw the branded header band (sync; call after loadPdfBrandAssets). */
export function drawPdfBrandHeader(doc, assets) {
  const pageW = doc.internal.pageSize.getWidth();
  const {
    logoDataUrl,
    logoW,
    logoH,
    logoY,
    taglineSize,
    taglineY,
    headerH,
  } = assets;
  let { useTextBrand } = assets;

  if (logoDataUrl) {
    try {
      doc.addImage(
        logoDataUrl,
        "PNG",
        (pageW - logoW) / 2,
        logoY,
        logoW,
        logoH,
      );
    } catch {
      useTextBrand = true;
    }
  }
  if (useTextBrand || !logoDataUrl) {
    doc.setTextColor(48, 45, 69);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(BRAND.name, pageW / 2, 16, { align: "center" });
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(taglineSize);
  doc.setTextColor(98, 93, 117);
  doc.text(BRAND.tagline, pageW / 2, taglineY, { align: "center" });

  return headerH;
}

/**
 * Draw the branded footer line + label.
 * Pass footerY (text baseline, mm) to place it mid-page (e.g. above a tear-off copy).
 * Defaults to the bottom of the current page.
 */
export function drawPdfBrandFooter(
  doc,
  { recordLabel = "records", marginX = 14, footerY } = {},
) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const y = footerY ?? pageH - 10;

  doc.setDrawColor(213, 204, 253);
  doc.setLineWidth(0.6);
  doc.line(marginX, y - 6, pageW - marginX, y - 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(122, 117, 139);
  doc.text(
    `${BRAND.name} Portal · Generated ${new Date().toLocaleString()} · For internal ${recordLabel}`,
    marginX,
    y,
  );
}

/**
 * Convenience for autoTable: header + footer on every page.
 * Use with margin.top ≈ assets.headerH + 6 and margin.bottom = PDF_BRAND_FOOTER_MARGIN.
 */
export function pdfBrandDidDrawPage(doc, assets, recordLabel, marginX = 14) {
  return () => {
    drawPdfBrandHeader(doc, assets);
    drawPdfBrandFooter(doc, { recordLabel, marginX });
  };
}

/**
 * Load assets, draw header once, return content start Y (below header + gap).
 * Footer should be drawn separately (once or via didDrawPage).
 */
export async function beginBrandedPdf(doc, { contentGap = 12 } = {}) {
  const assets = await loadPdfBrandAssets(doc);
  drawPdfBrandHeader(doc, assets);
  return {
    assets,
    contentStartY: assets.headerH + contentGap,
  };
}
